/**
 * ErrorSummaryChart Component
 *
 * Displays error statistics grouped by category with fix recommendations
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Copy, Loader2 } from "lucide-react";
import { useState } from "react";

interface ErrorCategory {
  category: string;
  count: number;
  tables: string[];
  recommendedFix?: string;
  sqlFixTemplate?: string;
}

interface ErrorSummaryChartProps {
  data?: {
    summary: ErrorCategory[];
    totalErrors: number;
    uniqueTables: number;
  };
  isLoading: boolean;
}

export function ErrorSummaryChart({ data, isLoading }: ErrorSummaryChartProps) {
  const [copiedCategory, setCopiedCategory] = useState<string | null>(null);

  const copySqlTemplate = (template: string, category: string) => {
    navigator.clipboard.writeText(template);
    setCopiedCategory(category);
    setTimeout(() => setCopiedCategory(null), 2000);
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      VARCHAR_TOO_SMALL: 'bg-red-500',
      NVARCHAR_TOO_SMALL: 'bg-red-400',
      DECIMAL_PRECISION: 'bg-orange-500',
      TINYINT_OVERFLOW: 'bg-orange-400',
      TIMEOUT: 'bg-yellow-500',
      CONNECTION_ERROR: 'bg-blue-500',
      OTHER: 'bg-gray-500',
    };

    return colors[category] || colors.OTHER;
  };

  const getCategoryLabel = (category: string): string => {
    return category.replace(/_/g, ' ');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.totalErrors === 0) {
    return (
      <Alert>
        <AlertDescription>
          No errors found in the selected time range. All pipelines succeeded!
        </AlertDescription>
      </Alert>
    );
  }

  const maxCount = Math.max(...data.summary.map(s => s.count));

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Error Analysis</CardTitle>
          <CardDescription>
            {data.totalErrors} total error{data.totalErrors !== 1 ? 's' : ''} across {data.uniqueTables} table{data.uniqueTables !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.summary.map((category) => {
              const percentage = (category.count / maxCount) * 100;

              return (
                <div key={category.category} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className={getCategoryColor(category.category)}>
                        {getCategoryLabel(category.category)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {category.count} error{category.count !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="text-sm font-medium">
                      {category.tables.length} table{category.tables.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${getCategoryColor(category.category)}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>

                  {/* Affected Tables */}
                  <div className="flex flex-wrap gap-1">
                    {category.tables.slice(0, 5).map((table, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {table}
                      </Badge>
                    ))}
                    {category.tables.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{category.tables.length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Fix Recommendations */}
      <div className="grid gap-4 md:grid-cols-2">
        {data.summary.map((category) => (
          <Card key={`fix-${category.category}`}>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Badge className={getCategoryColor(category.category)}>
                  {getCategoryLabel(category.category)}
                </Badge>
              </CardTitle>
              <CardDescription>{category.recommendedFix}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="text-xs font-medium text-muted-foreground">SQL Fix Template:</div>
                <div className="bg-muted rounded-md p-3">
                  <code className="text-xs font-mono">{category.sqlFixTemplate}</code>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => copySqlTemplate(category.sqlFixTemplate || '', category.category)}
                >
                  {copiedCategory === category.category ? (
                    'Copied!'
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1" />
                      Copy SQL Template
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Reference */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Reference</CardTitle>
          <CardDescription>Common error categories and solutions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-medium">Schema Issues (VARCHAR, NVARCHAR, DECIMAL, TINYINT)</div>
              <p className="text-muted-foreground">
                These errors occur when the target column size is too small for the source data.
                Run <code className="bg-muted px-1 py-0.5 rounded">apply-schema-fixes.sql</code> to automatically widen columns.
              </p>
            </div>
            <div>
              <div className="font-medium">Timeout Errors</div>
              <p className="text-muted-foreground">
                Large tables may exceed query timeout. Consider adding indexes, partitioning, or increasing timeout values.
              </p>
            </div>
            <div>
              <div className="font-medium">Connection Errors</div>
              <p className="text-muted-foreground">
                Check network connectivity, firewall rules, and ensure database credentials are correct.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
